import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

/**
 * SmsConsentManager
 * Props:
 *   - candidateId (required)
 *   - candidateName (required) — used in the suggested message template
 *   - roleTitle (optional) — fed into the consent link's role_context
 *   - onChange (optional) — called after generate / revoke so parent can refresh
 *
 * Renders a compact status section: badge + actions appropriate to the
 * current consent state (none → "Generate link"; pending → "Copy link" +
 * "Resend"; granted → details + "Revoke"; revoked → "Generate new link").
 */
const SmsConsentManager = ({ candidateId, candidateName, roleTitle, onChange }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // { has_active_consent, active_phone, records }
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('authToken')}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/sms/consent/status/candidate/${candidateId}`, { headers });
      setStatus(res.data);
    } catch (e) {
      console.error('Failed to load consent status', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [candidateId]);

  // ── Derive display state from records ─────────────────────────────────
  const records = status?.records || [];
  const activeRecord = records.find(r => r.status === 'granted' && !r.revoked_at);
  const pendingRecord = records.find(r => r.status === 'pending');
  const lastRevoked = records.find(r => r.status === 'revoked');

  let state = 'none';
  if (activeRecord) state = 'granted';
  else if (pendingRecord) state = 'pending';
  else if (lastRevoked) state = 'revoked';

  // ── Actions ──────────────────────────────────────────────────────────
  const generateLink = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/api/sms/consent/generate`,
        { candidate_id: candidateId, role_title: roleTitle || null },
        { headers }
      );
      setGeneratedUrl(res.data.consent_url);
      setExpanded(true);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to generate link');
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm('Revoke this candidate\'s SMS consent? They\'ll need to opt in again before you can text them.')) return;
    try {
      await axios.post(
        `${API}/api/sms/consent/revoke/${candidateId}`,
        { reason: 'manual' },
        { headers }
      );
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to revoke');
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      console.error('Clipboard write failed', e);
    }
  };

  // Suggested message text the recruiter pastes into Gmail or LinkedIn DM
  const suggestedMessage = (url) => {
    const first = (candidateName || '').split(' ')[0] || 'there';
    const roleClause = roleTitle ? ` about the ${roleTitle} opportunity` : '';
    return (
      `Hi ${first},\n\n` +
      `Quick admin step before I can text you${roleClause}: please tap the link below ` +
      `to confirm you're OK with me sending you SMS messages.\n\n` +
      `${url}\n\n` +
      `Takes about 10 seconds — just enter your number and tap the button. Once you're ` +
      `opted in I can send interview times, status updates, and quick questions by text.\n\n` +
      `Thanks!`
    );
  };

  // ── Render ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-xs text-gray-400 px-2 py-1.5">Loading SMS consent…</div>
    );
  }

  // Status pill colors
  const pillStyles = {
    none:    'bg-gray-100 text-gray-600 border-gray-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    granted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    revoked: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  const pillLabel = {
    none:    'SMS: not requested',
    pending: 'SMS: opt-in pending',
    granted: 'SMS: ✓ active',
    revoked: 'SMS: revoked',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Status header — always visible */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        <span className={`text-[11px] font-medium border rounded-full px-2 py-0.5 ${pillStyles[state]}`}>
          {pillLabel[state]}
        </span>
        <span className="text-[10px] text-gray-400">{expanded ? '▴' : '▾'}</span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-gray-100">
          {error && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              {error}
            </div>
          )}

          {/* GRANTED — show details + revoke */}
          {state === 'granted' && (
            <div className="space-y-2">
              <div className="text-[11px] text-gray-600 space-y-0.5">
                <div><span className="text-gray-400">Number:</span> {activeRecord.phone_number}</div>
                <div><span className="text-gray-400">Granted:</span> {new Date(activeRecord.granted_at).toLocaleString()}</div>
                <div><span className="text-gray-400">Via:</span> {activeRecord.granted_via || 'consent_link'}</div>
              </div>
              <button
                type="button"
                onClick={revoke}
                className="text-[11px] text-rose-600 hover:text-rose-700 hover:underline"
              >
                Revoke consent
              </button>
            </div>
          )}

          {/* NONE / REVOKED — generate first link */}
          {(state === 'none' || state === 'revoked') && !generatedUrl && (
            <div className="space-y-2">
              <p className="text-[11px] text-gray-600 leading-relaxed">
                {state === 'revoked'
                  ? 'Previous consent was revoked. Generate a new link to request opt-in again.'
                  : 'Generate a consent link to share with the candidate by email or LinkedIn. They\'ll confirm their number and you\'ll be cleared to send SMS.'}
              </p>
              <button
                type="button"
                onClick={generateLink}
                disabled={generating}
                className="w-full px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
              >
                {generating ? 'Generating…' : 'Generate consent link'}
              </button>
            </div>
          )}

          {/* PENDING — show existing link, allow resend/regenerate */}
          {state === 'pending' && !generatedUrl && (
            <div className="space-y-2">
              <p className="text-[11px] text-gray-600">
                Link sent — waiting for the candidate to opt in.
              </p>
              <LinkPanel
                url={`${window.location.origin}/sms-consent/${pendingRecord.token}`}
                message={suggestedMessage(`${window.location.origin}/sms-consent/${pendingRecord.token}`)}
                copiedField={copiedField}
                onCopy={copyToClipboard}
              />
              <button
                type="button"
                onClick={generateLink}
                disabled={generating}
                className="text-[11px] text-blue-600 hover:underline"
              >
                {generating ? 'Generating…' : 'Generate a new link instead'}
              </button>
            </div>
          )}

          {/* JUST GENERATED — show the freshly created URL */}
          {generatedUrl && (
            <div className="space-y-2">
              <p className="text-[11px] text-emerald-700 font-medium">
                ✓ Link generated. Copy the URL or full message and paste it into Gmail or LinkedIn.
              </p>
              <LinkPanel
                url={generatedUrl}
                message={suggestedMessage(generatedUrl)}
                copiedField={copiedField}
                onCopy={copyToClipboard}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Inner: URL + suggested message with per-field copy buttons.
const LinkPanel = ({ url, message, copiedField, onCopy }) => (
  <>
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-gray-500">Consent URL</span>
        <button
          type="button"
          onClick={() => onCopy(url, 'url')}
          className="text-[11px] text-blue-600 hover:underline"
        >
          {copiedField === 'url' ? '✓ Copied' : 'Copy URL'}
        </button>
      </div>
      <div className="text-[10px] font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 break-all">
        {url}
      </div>
    </div>
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-gray-500">Suggested message</span>
        <button
          type="button"
          onClick={() => onCopy(message, 'message')}
          className="text-[11px] text-blue-600 hover:underline"
        >
          {copiedField === 'message' ? '✓ Copied' : 'Copy message'}
        </button>
      </div>
      <pre className="text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 whitespace-pre-wrap font-sans leading-relaxed">{message}</pre>
    </div>
  </>
);

export default SmsConsentManager;
